const NO_QUEUED_SAVE = Symbol("NO_QUEUED_SAVE");

/**
 * Creates a single-flight queue for autosave calls.
 * While one save is in flight, later enqueues replace the queued value so only
 * the newest pending editor content is sent after the active save settles.
 */
export function createLatestOnlySaveQueue({ save, onStateChange = () => { } }) {
  let activeSave = NO_QUEUED_SAVE;
  let queuedSave = NO_QUEUED_SAVE;
  let isDraining = false;
  let drainPromise = Promise.resolve();

  const drain = async () => {
    if (isDraining) return drainPromise;

    drainPromise = (async () => {
      isDraining = true;
      onStateChange(true);

      try {
        while (queuedSave !== NO_QUEUED_SAVE) {
          const nextSave = queuedSave;
          queuedSave = NO_QUEUED_SAVE;
          activeSave = nextSave;

          try {
            await save(nextSave);
          } catch (_error) {
            if (queuedSave === NO_QUEUED_SAVE) break;
          } finally {
            activeSave = NO_QUEUED_SAVE;
          }
        }
      } finally {
        isDraining = false;
        if (queuedSave === NO_QUEUED_SAVE) {
          onStateChange(false);
        }
      }
    })();

    return drainPromise;
  };

  const enqueue = (value) => {
    if (
      isDraining &&
      activeSave === value &&
      queuedSave === NO_QUEUED_SAVE
    ) {
      return drainPromise;
    }

    queuedSave = value;
    onStateChange(true);
    return drain();
  };

  return {
    enqueue,
    isDraining: () => isDraining,
  };
}
